import { queryOptions } from '@tanstack/react-query';
import fetchClient from '@eureca/recruiter-api-sdk/client';

/**
 * Data layer da tela "Perfil do Candidato" (EU-594) do App do Consultor.
 *
 * Chama os proxies do BFF (`/candidates/:userId/profile` e
 * `/candidates/:userId/journey`) direto via `fetchClient` raw path — os
 * endpoints ainda não estão no SDK tipado (mesma receita da
 * stage-details-query). Os tipos espelham os response schemas do BFF
 * (candidates.routes.ts): as keys S3 de currículo/laudo NUNCA chegam ao
 * browser — só `hasResume`/`hasAttestation`.
 *
 * São DUAS queries independentes de propósito: o card "Trajetória na Eureca"
 * tem loading/empty/error próprios (PFC-016/031) e não pode derrubar a página
 * quando falha sozinho.
 */

export interface CandidateProfileUser {
  id: string;
  name: string | null;
  socialName: string | null;
  email: string | null;
  /** URL externa (SSO) — foto de plataforma NÃO tem URL, vem via blob. */
  avatarUrl: string | null;
  /** Existe foto (externa OU no bucket privado, servida por /files/avatar). */
  hasAvatar: boolean;
}

export interface CandidatePersonal {
  socialName: string | null;
  name: string | null;
  document: string | null;
  isForeigner: boolean;
  rg: string | null;
  mothersName: string | null;
  birthDate: string | null;
  email: string | null;
  phone: string | null;
  proLink: string | null;
}

export interface CandidateAddress {
  countryName: string | null;
  stateName: string | null;
  stateAcronym: string | null;
  cityName: string | null;
  cityText: string | null;
  zipcode: string | null;
  neighborhood: string | null;
  streetName: string | null;
  number: string | null;
  complement: string | null;
}

export interface CandidateDiversity {
  gender: string | null;
  ethnicity: string | null;
  sexualOrientation: string | null;
  /** Rótulo já resolvido pelo Core ("Sim"/"Não"/…), não boolean. */
  isPwd: string | null;
  pwdTypes: string[];
  hasAttestation: boolean;
}

export interface CandidateFormation {
  id: string;
  courseName: string | null;
  courseTypeName: string | null;
  institutionName: string | null;
  formationDate: string | null;
}

export interface CandidateProfileResponse {
  user: CandidateProfileUser;
  personal: CandidatePersonal;
  address: CandidateAddress;
  diversity: CandidateDiversity;
  formations: CandidateFormation[];
  hasResume: boolean;
  resumeFileName: string | null;
}

export type JourneyPhaseStatus = 'not_started' | 'in_progress' | 'completed';

export interface CandidateJourneyItem {
  subscriptionId: string;
  jobId: string;
  jobName: string | null;
  companyName: string | null;
  companyLogoUrl: string | null;
  programName: string | null;
  createdAt: string;
  cancelled: boolean;
  rejected: boolean;
  jobClosed: boolean;
  currentPhase: { name: string | null; status: JourneyPhaseStatus } | null;
  qualified: boolean;
  apt: boolean;
  workArea: { title: string | null; states: string[]; cities: string[] } | null;
}

export interface CandidateJourneyResponse {
  items: CandidateJourneyItem[];
}

export class CandidateProfileError extends Error {
  readonly name = 'CandidateProfileError';
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

/**
 * EU-686 RN-20/22 — 404 é "esse candidato não existe", não "a rede falhou".
 * A distinção decide DUAS coisas: a tela global de 404 no lugar do erro local
 * recuperável, e não insistir — nenhuma retentativa faz o candidato existir.
 */
export function isCandidateNotFound(error: unknown): boolean {
  return error instanceof CandidateProfileError && error.status === 404;
}

/**
 * Erro do cliente (4xx) é resposta definitiva do servidor: re-tentar só gera
 * requisição inútil e segura o usuário num skeleton. 5xx e falha de rede são
 * transitórios e valem uma segunda chance.
 */
function retryOnlyTransient(failureCount: number, error: unknown): boolean {
  const status = error instanceof CandidateProfileError ? error.status : 0;
  if (status >= 400 && status < 500) return false;
  return failureCount < 2;
}

export const candidateProfileQueryKey = (userId: string) =>
  ['recruiter', 'candidate-profile', userId] as const;

export const candidateJourneyQueryKey = (userId: string) =>
  ['recruiter', 'candidate-journey', userId] as const;

/** Perfil completo (dados pessoais, endereço, diversidade, formações). */
export function candidateProfileQueryOptions(userId: string) {
  return queryOptions({
    queryKey: candidateProfileQueryKey(userId),
    enabled: userId.length > 0,
    queryFn: async (): Promise<CandidateProfileResponse> => {
      const res = await fetchClient<CandidateProfileResponse>({
        method: 'GET',
        url: `/candidates/${userId}/profile`,
      });
      if (res.error) {
        throw new CandidateProfileError(
          `Falha ao carregar o perfil do candidato (status ${res.error.status})`,
          res.error.status
        );
      }
      return res.data;
    },
    staleTime: 60 * 1000,
    retry: retryOnlyTransient,
  });
}

/** Trajetória na Eureca (inscrições V2) — card lateral com estados próprios. */
export function candidateJourneyQueryOptions(userId: string) {
  return queryOptions({
    queryKey: candidateJourneyQueryKey(userId),
    enabled: userId.length > 0,
    queryFn: async (): Promise<CandidateJourneyResponse> => {
      const res = await fetchClient<CandidateJourneyResponse>({
        method: 'GET',
        url: `/candidates/${userId}/journey`,
      });
      if (res.error) {
        throw new CandidateProfileError(
          `Falha ao carregar a trajetória do candidato (status ${res.error.status})`,
          res.error.status
        );
      }
      return res.data;
    },
    staleTime: 60 * 1000,
    retry: retryOnlyTransient,
  });
}
